import type { PublicLineItem } from '@/types/public';

/**
 * Shape the public invoice payload's line items for rendering.
 *
 * The numeric fields are coerced because the wire carries Decimals as strings,
 * and `id`/`name` are defaulted so a line always has something to key and print.
 *
 * EVERYTHING ELSE IS CARRIED THROUGH UNTOUCHED, and that is the point. This
 * used to rebuild each line from a whitelist, which silently dropped every
 * field the shared line helpers read:
 *
 *   `type`, `chargeable`, `pricingTreatment`, `visibleToCustomer`,
 *   `hiddenFromCustomer`  — so `rendersOnCustomerDocument` saw an untyped,
 *                           unflagged line and printed everything, including a
 *                           non-chargeable material.
 *   `lineKey`, `parentLineKey`, `componentDisplayMode`
 *                         — so `customerFacingLines` could not group a service
 *                           with its parts and the NOT-590 rollup never ran.
 *   `priceStrategy`, `estimatedHours`, `actualHours`, `laborRate`
 *                         — so `tmBilledDisplay` could not print a time &
 *                           materials line on its billed basis.
 *
 * The server sends all of it (`invoiceLineRowsToJson` spreads `metadata` back
 * onto the row, and the public endpoint selects `invoiceLines` whole); only
 * this mapper lost it. A spread is what keeps the next helper from quietly
 * reading `undefined` — a whitelist has to be remembered, and was not.
 *
 * Spreading leaks nothing new: this shapes a payload the public endpoint has
 * already delivered to the browser.
 */
export function normalizePublicInvoiceLines(rawLines: unknown): PublicLineItem[] {
  if (!Array.isArray(rawLines)) return [];

  return rawLines.map((item, index) => {
    const line = (item ?? {}) as Partial<PublicLineItem>;
    const quantity = Number(line.quantity ?? 1);
    const unitPrice = Number(line.unitPrice ?? 0);

    return {
      ...line,
      id: line.id ?? `${index}`,
      name: line.name ?? line.description ?? `Line item ${index + 1}`,
      description: line.description ?? '',
      quantity,
      // Carried through so the Qty column can print the unit the same way the
      // invoice PDF does — one wording for the customer, both surfaces.
      unit: line.unit ?? null,
      unitPrice,
      discountPercent: Number(line.discountPercent ?? 0),
      taxRate: Number(line.taxRate ?? 0),
      lineTotal: Number(line.lineTotal ?? quantity * unitPrice),
    };
  });
}
